import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ACCOUNT_STATUS,
  STATUS_COPY,
  cancelAccount,
  getLifecycle,
  isGraceOpen,
  lockForCompliance,
  maybeWarnGracePeriod,
  noticesForSession,
  requestReactivate,
  retentionPolicyText,
  statusLabel,
  suspendAccount,
  unlockCompliance,
} from "../../js/api/accountLifecycle";
import { useAuth } from "../../js/hooks/useAuth";
import { hasBrokerAccess } from "../../js/auth/authService";
import { billingFreezeSummary } from "../../js/billing/lifecycleBilling";
import { formatRenewalDate } from "../../js/billing/billingDesk";
import { payUrl, JOURNEY_LINK_PROPS } from "../../js/utils/siteLinks";

const CARDS = [
  { id: "status", label: "Compliance Status" },
  { id: "suspend", label: "Suspend Account" },
  { id: "cancel", label: "Cancel Account" },
  { id: "reactivate", label: "Reactivate Account" },
  { id: "retention", label: "Data Retention Policy" },
  { id: "privacy", label: "Privacy Policy" },
  { id: "legal", label: "EULA & Disclaimer" },
  { id: "broker", label: "Broker Controls" },
];

export default function AccountLifecycle() {
  const { session, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const card = params.get("card") || "status";
  const [life, setLife] = useState(() => getLifecycle(session.userId));
  const [reason, setReason] = useState("");
  const [allowPortals, setAllowPortals] = useState(true);
  const [note, setNote] = useState("");
  const isBroker = hasBrokerAccess(session?.role);
  const policy = retentionPolicyText();
  const notices = useMemo(() => noticesForSession(session), [session, life.updated_at]);

  useEffect(() => {
    maybeWarnGracePeriod(session.userId);
    setLife(getLifecycle(session.userId));
  }, [session.userId]);

  function refresh() {
    setLife(getLifecycle(session.userId));
  }

  function go(id) {
    setParams({ tab: "lifecycle", card: id });
    setNote("");
  }

  function actor() {
    return { userId: session.userId, role: session.role };
  }

  function onSuspend() {
    suspendAccount(session.userId, actor(), { reason, allowClientPortals: allowPortals });
    setNote("This account is now suspended. Desk tools pause. Files stay with the office.");
    refresh();
  }

  function onCancel() {
    if (
      !window.confirm(
        "Cancel this professional account? Cancel billing at least 72 hours before renewal. A 30-day grace period stays open for a return with broker approval. Export anything you need first."
      )
    ) {
      return;
    }
    cancelAccount(session.userId, actor(), { reason });
    setNote("This account is cancelled. Transaction records remain with the office.");
    refresh();
  }

  function onReactivate() {
    try {
      requestReactivate(session.userId, actor());
      setNote("This account is active again, or a return request is with the broker.");
      refresh();
    } catch (error) {
      setNote(error.message);
    }
  }

  const copy = STATUS_COPY[life.status] || STATUS_COPY.active;
  const grace = isGraceOpen(life);
  const billing = billingFreezeSummary(session.userId);

  return (
    <div className="lifecycle-panel">
      <article className="card account-card">
        <h2>Account Lifecycle</h2>
        <p className="muted">
          Professional control of this REChecklist desk. Client buyer and seller checklists are never mixed here.
        </p>
        <p className={`lifecycle-status is-${life.status}`}>
          {statusLabel(life.status)}
          {life.status === ACCOUNT_STATUS.cancelled && grace ? " · Grace Period Active" : ""}
        </p>
        <p>{copy.desk}</p>
        <ul className="lifecycle-policy">
          <li>Active — desk and portals follow the office settings.</li>
          <li>Suspended — sign-in pauses; files stay; portals stay only if the broker allows.</li>
          <li>Cancelled — sign-in closes; a grace period may still allow a return.</li>
          <li>Compliance Lock — frozen for a quiet office review.</li>
          <li>Pending Reactivation — a return request is with the broker.</li>
          <li>Grace Period Active — a cancelled desk may still be restored with approval.</li>
          <li>
            User suspension — up to {policy.userSuspensionDays} days with billing frozen; original renewal
            date preserved on return.
          </li>
          <li>Office or admin suspension — up to {policy.adminSuspensionDays} days before automatic cancellation.</li>
        </ul>
        {billing.frozen ? (
          <p className="confirm-banner">
            Billing is paused. Anchor renewal date: {formatRenewalDate(billing.anchorDate)}.
            {billing.reason ? ` ${billing.reason}` : ""}
          </p>
        ) : null}
      </article>

      <nav className="lifecycle-cards" aria-label="Lifecycle sections">
        {CARDS.filter((item) => item.id !== "broker" || isBroker).map((item) => (
          <button
            key={item.id}
            type="button"
            className={`lifecycle-cards__btn${card === item.id ? " is-active" : ""}`}
            onClick={() => go(item.id)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {note ? <p className="confirm-banner">{note}</p> : null}

      {card === "status" ? (
        <article className="card account-card">
          <h2>Compliance Status</h2>
          <p>Current state: {statusLabel(life.status)}</p>
          {life.reason ? <p>Office note: {life.reason}</p> : null}
          {life.graceUntil ? <p>Grace period through {new Date(life.graceUntil).toLocaleDateString()}.</p> : null}
          {life.suspensionUntil ? (
            <p>
              Suspension window through {new Date(life.suspensionUntil).toLocaleDateString()}
              {life.suspensionKind ? ` (${life.suspensionKind})` : ""}.
            </p>
          ) : null}
          {billing.frozen ? (
            <p>Billing frozen since {formatRenewalDate(billing.frozenAt)}. Renewals resume when the desk returns.</p>
          ) : null}
          <h3>History</h3>
          <ul className="account-sessions">
            {(life.history || []).length ? (
              life.history.map((row) => (
                <li key={`${row.at}-${row.action}`}>
                  {new Date(row.at).toLocaleString()} · {row.action}
                </li>
              ))
            ) : (
              <li className="muted">No lifecycle events yet.</li>
            )}
          </ul>
          <h3>Notices</h3>
          <ul className="account-sessions">
            {notices.length ? (
              notices.map((row) => (
                <li key={row.id}>
                  {new Date(row.at).toLocaleString()} · {isBroker ? row.brokerText : row.agentText}
                </li>
              ))
            ) : (
              <li className="muted">No account notices on this device.</li>
            )}
          </ul>
        </article>
      ) : null}

      {card === "suspend" ? (
        <article className="card account-card">
          <h2>Suspend Account</h2>
          <p>
            Temporarily pause this desk. Login and Agent Desk pause. Billing freezes and your renewal anchor
            date is preserved for up to {policy.userSuspensionDays} days. Data stays intact.
          </p>
          <p className="muted">Use this for vacation, medical leave, or a quiet pause. The broker may suspend longer ({policy.adminSuspensionDays} days).</p>
          <label className="field">
            <span className="field-label">Note for the office (optional)</span>
            <textarea className="field-input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
          </label>
          {isBroker ? (
            <label className="choice-chip">
              <input type="checkbox" checked={allowPortals} onChange={(event) => setAllowPortals(event.target.checked)} />
              Allow assigned client portals to stay open
            </label>
          ) : (
            <p className="muted">Client portals stay open unless your broker closes them.</p>
          )}
          <button type="button" className="primary-btn" onClick={onSuspend}>
            Suspend this account
          </button>
        </article>
      ) : null}

      {card === "cancel" ? (
        <article className="card account-card">
          <h2>Cancel Account</h2>
          <p>
            Ends professional sign-in after a {policy.graceDays}-day reactivation grace period. Client
            portals close. Transaction and communication records stay with the office for retention.
            The broker is notified.
          </p>
          <p className="muted">
            Billing: cancel at least {policy.cancelNoticeHours || 72} hours before renewal to avoid
            the next charge. No refunds on completed billing periods. Export any desk records you need
            before you confirm — checklist and portfolio data on this device remain your
            responsibility to save or remove.
          </p>
          <label className="field">
            <span className="field-label">Note for the office (optional)</span>
            <textarea className="field-input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} />
          </label>
          <button type="button" className="primary-btn" onClick={onCancel}>
            Cancel this account
          </button>
          <button type="button" className="ghost-btn" onClick={() => logout()}>
            Sign out after cancelling
          </button>
        </article>
      ) : null}

      {card === "reactivate" ? (
        <article className="card account-card">
          <h2>Reactivate Account</h2>
          <p>
            A suspended desk can return immediately with billing resumed on the preserved renewal date. A
            cancelled desk can return during grace with broker approval, or via payment when billing was paused.
          </p>
          <div className="onboarding-actions">
            <button type="button" className="primary-btn" onClick={onReactivate}>
              Request or restore this account
            </button>
            <a className="ghost-btn" href={payUrl(session)} {...JOURNEY_LINK_PROPS}>
              Restore via payment
            </a>
          </div>
        </article>
      ) : null}

      {card === "retention" ? (
        <article className="card account-card">
          <h2>Data Retention Policy</h2>
          <ul className="lifecycle-policy">
            <li>Transaction data is kept for {policy.transactionYears} years.</li>
            <li>Client communications and attachments stay with those transaction records.</li>
            <li>The brokerage keeps archive access.</li>
            <li>Agent personal profile data is removed after the retention period.</li>
            <li>Cancellation includes a {policy.graceDays}-day grace period for a possible return.</li>
            <li>
              Billing cancellation requires {policy.cancelNoticeHours || 72} hours notice before
              renewal.
            </li>
            <li>Client checklist data is local — export from /backup on Windows PC or Apple Mac before closure.</li>
            <li>Restore points are capped and device-local; keep an exported backup for long-term retention.</li>
            <li>Suspended desks may lose write access until reactivated; export before suspension when possible.</li>
          </ul>
          <p className="field-hint">{policy.summary}</p>
        </article>
      ) : null}

      {card === "privacy" ? (
        <article className="card account-card">
          <h2>Privacy Policy</h2>
          <p>Your account and client data are managed with strict privacy and security controls.</p>
          <ul className="lifecycle-policy">
            <li>Suspension pauses the desk. Files stay. Nothing is deleted.</li>
            <li>Cancellation closes the desk. Required transaction records remain with the office.</li>
            <li>A compliance lock freezes sign-in, portals, and edits until the broker releases it.</li>
            <li>Reactivation restores the desk and, when allowed, client portals.</li>
            <li>Buyer and seller journeys stay on separate carpets.</li>
          </ul>
          <p>
            <Link to="/privacy">Read the public privacy page</Link>
          </p>
        </article>
      ) : null}

      {card === "legal" ? (
        <article className="card account-card">
          <h2>EULA &amp; Disclaimer</h2>
          <p>
            Launch legal terms cover supported systems (Windows PC and Apple Mac), your duty to export
            and retain data, restore point limits, and suspension behavior.
          </p>
          <ul className="lifecycle-policy">
            <li>
              <Link to="/eula">End User License Agreement</Link>
            </li>
            <li>
              <Link to="/disclaimer">Disclaimer</Link>
            </li>
            <li>
              <Link to="/terms">Terms of use</Link>
            </li>
            <li>
              <Link to="/backup">Backup &amp; export desk</Link>
            </li>
            <li>
              <Link to="/restore">Restore points desk</Link>
            </li>
          </ul>
        </article>
      ) : null}

      {card === "broker" && isBroker ? (
        <article className="card account-card">
          <h2>Broker Controls</h2>
          <p>Review agent status, freeze a desk for a quiet compliance review, and restore access.</p>
          <p>
            <Link className="primary-btn" to="/broker/controls/agents">
              Open agent controls
            </Link>
          </p>
          {session.role === "broker" && life.status === ACCOUNT_STATUS.compliance_lock ? (
            <button type="button" className="ghost-btn" onClick={() => { unlockCompliance(session.userId, actor()); refresh(); }}>
              Release my own lock
            </button>
          ) : null}
          {session.role === "broker" ? (
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                lockForCompliance(session.userId, actor(), { reason: "Self lock" });
                refresh();
              }}
            >
              Lock my desk for review
            </button>
          ) : null}
        </article>
      ) : null}
    </div>
  );
}
